import { formatCurrency } from '@/app/utils/formatCurrency';
import { formatIsoDateOnlyShort } from '@/app/utils/formatDate';
import { dueText } from './dueText';
import { PARTLY_INVOICED_NOTE } from './invoiceSelection';
import type { InvoiceableService } from './invoiceableLines';
import { isOverdue } from './overdue';
import { addOnLineScaled, fromScaled, itemLineScaled, toScaled } from './soldLineAmounts';
import type { StillToInvoiceContent } from './stillToInvoice';

export interface StillToInvoiceServiceRow {
    id: string;
    name: string;
    note: string | null;
    net: string;
    due: { date: string | null; mileage: string | null };
    overdue: boolean;
}

export interface StillToInvoiceAddOnRow {
    id: string;
    name: string;
    quantity: string | null;
    linkedTo: string | null;
    net: string;
    dueDate: string | null;
    overdue: boolean;
}

export interface StillToInvoicePanelModel {
    services: StillToInvoiceServiceRow[];
    addOns: StillToInvoiceAddOnRow[];
    emptyMessages: { services: string; addOns: string };
    lineCount: number;
    totalNet: string;
}

const money = (scaled: number): string => formatCurrency(fromScaled(scaled));

export function remainingServiceNetScaled(service: InvoiceableService): number {
    if (!service.partlyInvoiced) return toScaled(service.net);
    return service.items.reduce((sum, item) => sum + itemLineScaled(item).net, 0);
}

export function stillToInvoicePanel(
    content: Extract<StillToInvoiceContent, { everythingInvoiced: false }>,
    today: string,
): StillToInvoicePanelModel {
    const serviceNets = content.services.map(remainingServiceNetScaled);
    const addOnNets = content.addOns.map((addOn) => addOnLineScaled(addOn).net);
    const totalScaled = [...serviceNets, ...addOnNets].reduce((sum, net) => sum + net, 0);

    return {
        services: content.services.map((service, index) => ({
            id: service.id,
            name: service.name,
            note: service.partlyInvoiced ? PARTLY_INVOICED_NOTE : null,
            net: money(serviceNets[index]),
            due: dueText(service.dueDate, service.dueMileage),
            overdue: isOverdue(service.dueDate, today),
        })),
        addOns: content.addOns.map((addOn, index) => ({
            id: addOn.id,
            name: addOn.name,
            quantity: addOn.quantity > 1 ? `× ${addOn.quantity}` : null,
            linkedTo: addOn.linkedServiceId ? (content.serviceNames.get(addOn.linkedServiceId) ?? null) : null,
            net: money(addOnNets[index]),
            dueDate: addOn.dueDate ? formatIsoDateOnlyShort(addOn.dueDate) : null,
            overdue: isOverdue(addOn.dueDate, today),
        })),
        emptyMessages: content.emptyMessages,
        lineCount: content.services.length + content.addOns.length,
        totalNet: money(totalScaled),
    };
}
